import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { useAuth, hasPermission, isAdmin } from "../context/AuthContext";
import { isProductionLoadingSupervisorRole } from "../lib/dashboardRole";
import { useWS } from "../context/WebSocketContext";
import { PageHeader, SectionCard, StatePill, Pill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { formatIndiaDateTime } from "../lib/indiaTime";
import { formatListDate } from "../lib/purchaseListMeta";
import { formatWeightKg } from "../lib/weightUnits";
import LoadingReceiptActions from "../components/LoadingReceiptActions";
import OutwardWeighmentReceiptActions from "../components/OutwardWeighmentReceiptActions";
import DispatchDocsUpload from "../components/DispatchDocsUpload";
import AccountsSalesSettlementPanel from "./accounts/AccountsSalesSettlementPanel";
import DataTableBlock from "../components/DataTableBlock";
import { useDataTable } from "../hooks/useDataTable";
import {
  LOADING_REGISTER_COLUMNS,
  SALES_LINK_REGISTER_COLUMNS,
} from "./outwardDispatchColumns";
import OutwardTruckBookingsPanel from "../components/OutwardTruckBookingsPanel";
import { salesModuleFromKey } from "../lib/salesModuleConfig";

const STATE_LABELS = {
  REGISTERED: "Awaiting sales link",
  SALES_LINKED: "Awaiting tare weigh",
  WEIGH_TARE: "Awaiting loading",
  LOADED: "Awaiting gross weigh",
  WEIGH_GROSS: "Gross recorded",
  ACCOUNTS_PENDING: "Awaiting provisional / final",
  COMPLETED: "Completed",
  REJECTED: "Rejected",
};

function DispatchCard({ d, children, actions, focused }) {
  return (
    <div
      id={`outward-dispatch-${d.id}`}
      className={`rounded-lg border bg-card p-4 space-y-3 ${
        focused ? "border-bansal-sage ring-2 ring-bansal-sage/30" : "border-border"
      }`}
      data-testid={`outward-dispatch-${d.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-sm font-semibold">{d.code}</p>
          <p className="text-sm font-medium uppercase mt-0.5">{d.vehicle_number || "—"}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Gate {d.gate_pass_code}
            {d.party_name ? ` · ${d.party_name}` : ""}
            {d.sales_contract_number ? ` · ${d.sales_contract_number}` : ""}
            {d.sales_voucher_code ? ` · Voucher ${d.sales_voucher_code}` : ""}
          </p>
        </div>
        <StatePill state={d.state} label={STATE_LABELS[d.state] || d.state} />
      </div>
      {(d.tare_weight_qtl != null || d.gross_weight_qtl != null) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div>
            <span className="text-muted-foreground">Tare</span>
            <p className="font-medium tabular">{formatWeightKg(d.tare_weight_qtl) ?? "—"}</p>
            {d.tare_weighed_at && (
              <p className="text-[10px] text-muted-foreground">{formatIndiaDateTime(d.tare_weighed_at)}</p>
            )}
          </div>
          <div>
            <span className="text-muted-foreground">Gross</span>
            <p className="font-medium tabular">{formatWeightKg(d.gross_weight_qtl) ?? "—"}</p>
            {d.gross_weighed_at && (
              <p className="text-[10px] text-muted-foreground">{formatIndiaDateTime(d.gross_weighed_at)}</p>
            )}
          </div>
          <div>
            <span className="text-muted-foreground">Net</span>
            <p className="font-medium tabular">{formatWeightKg(d.net_weight_qtl) ?? "—"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Bags</span>
            <p className="font-medium tabular">{d.bags_loaded ?? "—"}</p>
          </div>
        </div>
      )}
      {children}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {actions}
        {d.loaded_at && (
          <LoadingReceiptActions dispatchId={d.id} label={d.gate_pass_code || d.code} compact />
        )}
        {(d.tare_weight_qtl != null || d.gross_weight_qtl != null) && (
          <OutwardWeighmentReceiptActions dispatchId={d.id} label={d.code} compact />
        )}
      </div>
    </div>
  );
}

function SalesLinkPanel({ dispatch, contracts, onSaved }) {
  const state = String(dispatch?.state || "").toUpperCase();
  const isEdit = state !== "REGISTERED" && state !== "";
  const loadedAlready = !["", "REGISTERED", "SALES_LINKED", "WEIGH_TARE"].includes(state);
  const existingAllocs = dispatch?.contract_allocations || [];

  const buildRowFromContract = (c, expectedWeight = "") => {
    const orderQty = Number(c.quantity) || 0;
    const dispatched = Number(c.dispatched_qtl) || 0;
    const onThisTruck = existingAllocs
      .filter((a) => String(a.sales_contract_id) === String(c.id))
      .reduce((s, a) => s + Number(a.expected_weight_qtl || a.net_weight_qtl || 0), 0);
    const baseHeadroom = c.remaining_headroom_qtl != null
      ? Number(c.remaining_headroom_qtl)
      : Math.max(0, Math.round(orderQty * 1.1 * 100) / 100 - dispatched);
    // When editing, this truck's current qty is already inside dispatched — add it back.
    const headroom = Math.round((baseHeadroom + onThisTruck) * 100) / 100;
    return {
      sales_contract_id: Number(c.id),
      expected_weight_qtl: expectedWeight !== "" && expectedWeight != null ? String(expectedWeight) : "",
      order_qty: orderQty,
      dispatched_qtl: Math.max(0, Math.round((dispatched - onThisTruck) * 100) / 100),
      remaining_headroom_qtl: headroom,
      contract_number: c.contract_number,
      variety: c.variety,
      item_name: c.item_name || c.variety,
      status: c.status,
    };
  };

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const activeContracts = useMemo(
    () => (contracts || []).filter((c) => String(c.status || "").toUpperCase() === "ACTIVE"),
    [contracts],
  );

  useEffect(() => {
    if (!existingAllocs.length) {
      setRows([]);
      return;
    }
    const next = [];
    for (const a of existingAllocs) {
      const c = activeContracts.find((x) => String(x.id) === String(a.sales_contract_id));
      if (!c) {
        // Contract may still be ACTIVE but missing from list — keep a minimal row.
        next.push({
          sales_contract_id: Number(a.sales_contract_id),
          expected_weight_qtl: String(a.expected_weight_qtl || a.net_weight_qtl || ""),
          order_qty: 0,
          dispatched_qtl: 0,
          remaining_headroom_qtl: Infinity,
          contract_number: a.contract_number || String(a.sales_contract_id),
          variety: a.variety,
          item_name: a.variety,
          status: "ACTIVE",
        });
        continue;
      }
      next.push(buildRowFromContract(c, a.expected_weight_qtl || a.net_weight_qtl || ""));
    }
    setRows(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed when dispatch or contract list changes
  }, [dispatch?.id, dispatch?.state, existingAllocs.length, activeContracts.length]);

  const toggle = (c) => {
    const id = String(c.id);
    setRows((prev) => {
      if (prev.some((r) => String(r.sales_contract_id) === id)) {
        return prev.filter((r) => String(r.sales_contract_id) !== id);
      }
      const prior = existingAllocs.find((a) => String(a.sales_contract_id) === id);
      return [
        ...prev,
        buildRowFromContract(c, prior?.expected_weight_qtl || prior?.net_weight_qtl || ""),
      ];
    });
  };

  const updateRow = (id, patch) => {
    setRows((prev) =>
      prev.map((r) => (String(r.sales_contract_id) !== String(id) ? r : { ...r, ...patch })),
    );
  };

  const submit = async () => {
    if (!rows.length) {
      toast.error("Select at least one sales contract");
      return;
    }
    for (const r of rows) {
      const wt = Number(r.expected_weight_qtl);
      if (!wt || wt <= 0) {
        toast.error(`Enter expected weight (qtl) for ${r.contract_number}`);
        return;
      }
      const headroom = Number(r.remaining_headroom_qtl);
      if (Number.isFinite(headroom) && wt > headroom + 1e-6) {
        toast.error(
          `${r.contract_number}: this truck ${wt} qtl + already dispatched ${r.dispatched_qtl} qtl ` +
            `exceeds order ${r.order_qty} qtl +10% (max this truck ${headroom} qtl)`,
        );
        return;
      }
    }
    try {
      setBusy(true);
      const allocations = rows.map((r) => ({
        sales_contract_id: Number(r.sales_contract_id),
        expected_weight_qtl: Number(r.expected_weight_qtl),
        planned_qtl: Number(r.expected_weight_qtl),
      }));
      await api.post(`/outward-dispatches/${dispatch.id}/link-sales`, {
        sales_contract_id: allocations[0].sales_contract_id,
        allocations,
        allow_correction: isEdit,
      });
      toast.success(isEdit ? "Sales link updated" : "Party and contract linked — load when stock is packed");
      onSaved?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 pt-2 border-t border-border/60">
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {isEdit
          ? loadedAlready
            ? "Edit or change the sales link. The contract item must match what was loaded."
            : "Edit or change the sales link · pick contracts and expected weight"
          : "Active sales contracts · link without stock; loading needs packed bags"}
      </Label>
      <div className="max-h-56 overflow-y-auto rounded-md border border-border/70 divide-y">
        {activeContracts.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">No active sales contracts.</p>
        ) : (
          activeContracts.map((c) => {
            const id = String(c.id);
            const checked = rows.some((r) => String(r.sales_contract_id) === id);
            const qty = Number(c.quantity) || 0;
            const dispatched = Number(c.dispatched_qtl) || 0;
            const onThisTruck = existingAllocs
              .filter((a) => String(a.sales_contract_id) === id)
              .reduce((s, a) => s + Number(a.expected_weight_qtl || a.net_weight_qtl || 0), 0);
            const headroom = Math.round(
              ((c.remaining_headroom_qtl != null
                ? Number(c.remaining_headroom_qtl)
                : Math.max(0, Math.round(qty * 1.1 * 100) / 100 - dispatched)) + onThisTruck) * 100,
            ) / 100;
            return (
              <label key={id} className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/40">
                <input type="checkbox" className="mt-1" checked={checked} onChange={() => toggle(c)} />
                <span>
                  <span className="font-mono text-xs">{c.contract_number}</span>
                  {" · "}
                  {c.buyer_name}
                  {" · "}
                  {c.product_family === "RICE_BRAN"
                    ? `Bran · ${c.pricing_mode === "PER_OIL_POINT" ? "oil-pt" : "fixed"}`
                    : c.variety}
                  {c.item_name ? ` · ${c.item_name}` : ""}
                  {qty > 0 ? (
                    <span className="text-muted-foreground">
                      {" · "}order {qty} qtl · other trucks {Math.max(0, dispatched - onThisTruck)} · left ≤ {headroom}
                    </span>
                  ) : null}
                </span>
              </label>
            );
          })
        )}
      </div>
      {rows.length > 0 ? (
        <div className="space-y-2 rounded-md border border-border/70 p-2">
          {rows.map((r) => (
            <div key={r.sales_contract_id} className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-end">
              <p className="text-xs sm:col-span-2 font-mono">
                {r.contract_number}
                {r.item_name ? ` · ${r.item_name}` : ""}
                {r.order_qty > 0
                  ? ` · other trucks ${r.dispatched_qtl} qtl · max this truck ${r.remaining_headroom_qtl} qtl`
                  : ""}
              </p>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Expected weight (qtl)
                </Label>
                <Input
                  type="number"
                  min="0"
                  max={Number.isFinite(r.remaining_headroom_qtl) && r.remaining_headroom_qtl > 0 ? r.remaining_headroom_qtl : undefined}
                  step="0.01"
                  className="mt-1 h-9 max-w-xs"
                  value={r.expected_weight_qtl}
                  onChange={(e) => updateRow(r.sales_contract_id, { expected_weight_qtl: e.target.value })}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <Button type="button" size="sm" disabled={busy} onClick={submit} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
        {busy ? (isEdit ? "Updating…" : "Linking…") : isEdit ? "Save link" : "Link party & contract"}
      </Button>
    </div>
  );
}

function batchStatusLabel(status) {
  if (status === "UNLOADING_ACTIVE") return "Unloading";
  if (status === "MILLING_ONLY") return "Milling";
  if (status === "ARCHIVED_COMPLETED") return "Archived";
  if (status === "PLANNED") return "Planned";
  return status || "";
}

function LoadingPanel({ dispatch, onSaved, revise = false }) {
  const [workspace, setWorkspace] = useState(null);
  const [picks, setPicks] = useState({});
  const [notes, setNotes] = useState(() => (revise ? dispatch.loading_notes || "" : ""));
  const [busy, setBusy] = useState(false);
  const [localDocs, setLocalDocs] = useState(() => dispatch.settlement_docs || []);

  useEffect(() => {
    setLocalDocs(dispatch.settlement_docs || []);
  }, [dispatch.id, dispatch.settlement_docs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/outward-dispatches/${dispatch.id}/loading-workspace`);
        if (cancelled) return;
        setWorkspace(data || null);
        const next = {};
        (data?.batch_sources || []).forEach((s) => {
          const status = String(s.status || "").toUpperCase();
          if (status === "LOADED" || status === "FINALIZED") {
            const key = `${s.production_batch_id}|${s.item_id}|${s.weight_per_bag}`;
            next[key] = String(s.bags_loaded || "");
          }
        });
        setPicks(next);
      } catch (err) {
        toast.error(extractError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch.id]);

  const targetsByItem = useMemo(() => {
    const map = new Map();
    (workspace?.targets || []).forEach((t) => {
      if (!t.item_id) return;
      const cur = map.get(t.item_id) || {
        item_id: t.item_id,
        item_name: t.item_name,
        variety: t.variety,
        expected_bags: 0,
        expected_weight_qtl: 0,
        contracts: [],
      };
      cur.expected_bags += Number(t.expected_bags || 0);
      cur.expected_weight_qtl += Number(t.expected_weight_qtl || 0);
      cur.contracts.push(t.contract_number);
      map.set(t.item_id, cur);
    });
    return [...map.values()];
  }, [workspace]);

  const stockByItem = useMemo(() => {
    const map = new Map();
    (workspace?.stock_lines || []).forEach((line) => {
      const list = map.get(line.item_id) || [];
      list.push(line);
      map.set(line.item_id, list);
    });
    return map;
  }, [workspace]);

  const pickGroups = useMemo(() => {
    if (!revise) return targetsByItem;
    const groups = [...targetsByItem];
    const known = new Set(groups.map((t) => Number(t.item_id)));
    stockByItem.forEach((lines, itemId) => {
      if (known.has(Number(itemId))) return;
      const line = lines[0];
      groups.push({
        item_id: itemId,
        item_name: line?.item_name || `Item ${itemId}`,
        variety: "",
        expected_bags: 0,
        expected_weight_qtl: 0,
        contracts: [],
      });
    });
    return groups;
  }, [revise, targetsByItem, stockByItem]);

  const setPick = (line, bags) => {
    const key = `${line.batch_id}|${line.item_id}|${line.weight_per_bag}`;
    setPicks((prev) => {
      const next = { ...prev };
      if (!bags || Number(bags) <= 0) delete next[key];
      else next[key] = bags;
      return next;
    });
  };

  const allocatedWeightForItem = (itemId) =>
    Object.entries(picks).reduce((sum, [key, bags]) => {
      const parts = key.split("|");
      if (Number(parts[1]) !== Number(itemId)) return sum;
      const wpb = Number(parts[2]) || 0;
      const n = Number(bags || 0);
      if (!n || !wpb) return sum;
      return sum + (n * wpb) / 100;
    }, 0);

  const allocatedForItem = (itemId) =>
    Object.entries(picks).reduce((sum, [key, bags]) => {
      const parts = key.split("|");
      if (Number(parts[1]) !== Number(itemId)) return sum;
      return sum + Number(bags || 0);
    }, 0);

  const submit = async () => {
    const batch_sources = Object.entries(picks)
      .map(([key, bags]) => {
        const [batchId, itemId, wpb] = key.split("|");
        const n = Number(bags);
        if (!n || n <= 0) return null;
        return {
          production_batch_id: Number(batchId),
          item_id: Number(itemId),
          weight_per_bag: Number(wpb),
          bags_loaded: n,
        };
      })
      .filter(Boolean);
    if (!batch_sources.length) return toast.error("Allocate bags from at least one batch");
    for (const t of targetsByItem) {
      const targetWt = Number(t.expected_weight_qtl || 0);
      if (targetWt > 0) {
        const gotWt = allocatedWeightForItem(t.item_id);
        const tol = Math.max(0.05, targetWt * 0.1);
        if (Math.abs(gotWt - targetWt) > tol) {
          const lo = Math.round(targetWt * 0.9 * 100) / 100;
          const hi = Math.round(targetWt * 1.1 * 100) / 100;
          return toast.error(
            `${t.item_name || "Item"}: allocated ${gotWt.toFixed(2)} qtl must be within ±10% of target ${targetWt.toFixed(2)} qtl (${lo}–${hi})`,
          );
        }
        continue;
      }
      const got = allocatedForItem(t.item_id);
      if (t.expected_bags > 0 && got !== t.expected_bags) {
        return toast.error(
          `${t.item_name || "Item"}: allocated ${got} bags but target is ${t.expected_bags}`,
        );
      }
    }
    const totalBags = batch_sources.reduce((s, r) => s + r.bags_loaded, 0);
    try {
      setBusy(true);
      if (revise) {
        await api.patch(`/outward-dispatches/${dispatch.id}/loading-correction`, {
          bags_loaded: totalBags,
          loading_notes: notes.trim() || undefined,
          batch_sources,
        });
        const settled =
          String(dispatch.state || "").toUpperCase() === "COMPLETED" ||
          String(dispatch.sales_settlement_status || "").toUpperCase() === "FINAL";
        toast.success(
          settled
            ? "Loading updated. Sales settlement reopened for accounts."
            : "Loading updated",
        );
      } else {
        await api.post(`/outward-dispatches/${dispatch.id}/complete-loading`, {
          bags_loaded: totalBags,
          loading_notes: notes.trim() || undefined,
          batch_sources,
        });
        toast.success("Loading complete — stock reserved on selected batches until gross weigh");
      }
      onSaved?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 pt-2 border-t border-border/60">
      <div className="rounded-md border border-border/70 bg-muted/20 p-3 space-y-1">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Loading target</p>
        <p className="text-sm font-medium">
          {workspace?.total_expected_weight_qtl || 0} qtl
        </p>
        {targetsByItem.length ? (
          <ul className="text-xs text-muted-foreground space-y-0.5">
            {targetsByItem.map((t) => {
              const targetWt = Number(t.expected_weight_qtl || 0);
              const gotWt = allocatedWeightForItem(t.item_id);
              const tol = Math.max(0.05, targetWt * 0.1);
              const wtOk = targetWt <= 0 || Math.abs(gotWt - targetWt) <= tol;
              return (
                <li key={t.item_id}>
                  {t.item_name || t.variety || `Item ${t.item_id}`}
                  {": "}
                  {Math.round(targetWt * 100) / 100} qtl
                  {" · allocated "}
                  <span className={wtOk ? "text-foreground" : "text-amber-700"}>
                    {gotWt.toFixed(2)} qtl
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">No reserved targets — pick any available stock.</p>
        )}
      </div>

      {targetsByItem.length === 0 && !(workspace?.stock_lines || []).length ? (
        <p className="text-sm text-muted-foreground py-2">
          No batch stock available for linked items yet. Production supervisor packs on the
          packing register first, then allocate bags here to load.
        </p>
      ) : null}

      {(pickGroups.length ? pickGroups : [{ item_id: null, item_name: "All stock" }]).map((t) => {
        const lines = t.item_id
          ? stockByItem.get(t.item_id) || []
          : workspace?.stock_lines || [];
        if (!lines.length && t.item_id) {
          return (
            <p key={t.item_id || "all"} className="text-xs text-amber-700">
              No stock yet for {t.item_name || t.item_id}. Production must pack this item
              on a batch before loading.
            </p>
          );
        }
        return (
          <div key={t.item_id || "all"} className="space-y-2">
            {t.item_id ? (
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t.item_name} — pick batches
              </p>
            ) : null}
            <div className="rounded-md border border-border/70 divide-y">
              {lines.map((line) => {
                const key = `${line.batch_id}|${line.item_id}|${line.weight_per_bag}`;
                const val = picks[key] || "";
                return (
                  <div key={key} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                    <div className="min-w-[10rem] flex-1">
                      <p className="font-mono text-xs">{line.batch_code}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {batchStatusLabel(line.batch_status)} · {line.item_name} · {line.weight_per_bag} kg
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Available {line.bags_available} bags
                        {line.available_weight_qtl != null ? ` (~${line.available_weight_qtl} qtl)` : ""}
                        {line.bags_reserved ? ` · reserved ${line.bags_reserved}` : ""}
                      </p>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      max={line.bags_available || undefined}
                      className="h-9 w-24"
                      placeholder="Bags"
                      value={val}
                      onChange={(e) => setPick(line, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Loading notes</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" />
      </div>
      <div className="rounded-md border border-border/70 p-3 space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Truck documents</p>
        <DispatchDocsUpload
          dispatchId={dispatch.id}
          docs={localDocs}
          hint="Upload RC, owner cheque, driver Aadhaar now or later at accounts."
          onUploaded={(next) => {
            if (next?.settlement_docs) setLocalDocs(next.settlement_docs);
            onSaved?.();
          }}
        />
      </div>
      <Button
        type="button"
        size="sm"
        disabled={busy || !Object.keys(picks).length}
        onClick={submit}
        className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
      >
        {busy ? "Saving…" : revise ? "Save loading correction" : "Confirm loading complete"}
      </Button>
    </div>
  );
}

export default function OutwardDispatchPage({ moduleKey = "link" }) {
  const { user } = useAuth();
  const moduleConfig = salesModuleFromKey(moduleKey);
  const loadingDesk = isProductionLoadingSupervisorRole(user?.role);
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({
    dispatches: [],
    sales_contracts: [],
    loading_register: [],
    sales_link_register: [],
    sales_voucher_register: [],
    loading_register_count: 0,
    sales_link_register_count: 0,
    sales_voucher_register_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerLoaded, setRegisterLoaded] = useState(false);
  const [pageTab, setPageTab] = useState("queue");
  const [linkEditId, setLinkEditId] = useState(null);
  const [linkEditDispatch, setLinkEditDispatch] = useState(null);
  const [linkEditLoading, setLinkEditLoading] = useState(false);
  const [loadEditId, setLoadEditId] = useState(null);
  const focusDispatchId = searchParams.get("dispatch") || "";
  const adminUser = isAdmin(user);

  const canLinkSales = hasPermission(user, "outward.link_sales");
  const canLoad = hasPermission(user, "outward.load");
  const canAccounts = hasPermission(user, "outward.accounts_finalize");
  const activeTab = moduleConfig.tab;

  const bootstrapModule =
    activeTab === "sales" ? "link" : activeTab === "loading" ? "loading" : "voucher";
  const registerKind =
    activeTab === "sales" ? "sales_link" : activeTab === "loading" ? "loading" : "sales_voucher";

  const load = useCallback(() => {
    // Sales voucher desk is fully owned by AccountsSalesSettlementPanel.
    if (bootstrapModule === "voucher") {
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = { module: bootstrapModule };
    api
      .get("/outward-dispatches/bootstrap", { params })
      .then((r) =>
        setData((prev) => ({
          ...prev,
          dispatches: r.data?.dispatches || [],
          sales_contracts: r.data?.sales_contracts || [],
          loading_register_count: r.data?.loading_register_count ?? prev.loading_register_count ?? 0,
          sales_link_register_count: r.data?.sales_link_register_count ?? prev.sales_link_register_count ?? 0,
          sales_voucher_register_count:
            r.data?.sales_voucher_register_count ?? prev.sales_voucher_register_count ?? 0,
        })),
      )
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  }, [bootstrapModule]);

  const loadRegister = useCallback(
    (opts = {}) => {
      const { background = false } = opts;
      if (!background) setRegisterLoading(true);
      return api
        .get("/outward-dispatches/register", { params: { kind: registerKind, limit: 100 } })
        .then((r) => {
          const rows = r.data?.results || [];
          const count = r.data?.count ?? rows.length;
          setData((prev) => {
            if (registerKind === "sales_link") {
              return { ...prev, sales_link_register: rows, sales_link_register_count: count };
            }
            if (registerKind === "loading") {
              return { ...prev, loading_register: rows, loading_register_count: count };
            }
            return { ...prev, sales_voucher_register: rows, sales_voucher_register_count: count };
          });
          setRegisterLoaded(true);
        })
        .catch((e) => {
          if (!background) toast.error(extractError(e));
        })
        .finally(() => {
          if (!background) setRegisterLoading(false);
        });
    },
    [registerKind],
  );

  useEffect(() => {
    load();
    setRegisterLoaded(false);
  }, [load]);

  useEffect(() => {
    setPageTab("queue");
    setRegisterLoaded(false);
  }, [moduleKey]);

  // After queue paints, prefetch register in background so Register tab feels instant.
  useEffect(() => {
    if (loading || registerLoaded || activeTab === "accounts") return;
    const t = window.setTimeout(() => {
      loadRegister({ background: true });
    }, 400);
    return () => window.clearTimeout(t);
  }, [loading, registerLoaded, activeTab, loadRegister]);

  // If user opens Register before prefetch finishes, load immediately.
  useEffect(() => {
    if (pageTab !== "register" || registerLoaded || registerLoading) return;
    loadRegister();
  }, [pageTab, registerLoaded, registerLoading, loadRegister]);

  const openSalesLinkEdit = useCallback(async (row) => {
    setLinkEditId(row.id);
    setLinkEditLoading(true);
    setLinkEditDispatch(null);
    try {
      const { data: full } = await api.get(`/outward-dispatches/${row.id}`);
      setLinkEditDispatch(full);
    } catch (err) {
      toast.error(extractError(err));
      setLinkEditId(null);
    } finally {
      setLinkEditLoading(false);
    }
  }, []);

  const closeSalesLinkEdit = useCallback(() => {
    setLinkEditId(null);
    setLinkEditDispatch(null);
    setLinkEditLoading(false);
  }, []);

  const onSalesLinkEdited = useCallback(() => {
    closeSalesLinkEdit();
    load();
    loadRegister({ background: true });
  }, [closeSalesLinkEdit, load, loadRegister]);

  const { subscribe } = useWS();
  useEffect(
    () =>
      subscribe("outward_dispatch", () => {
        load();
        if (pageTab === "register" || registerLoaded) loadRegister({ background: true });
      }),
    [subscribe, load, loadRegister, pageTab, registerLoaded],
  );

  const canAdminEditSalesLink = (d) => {
    if (!adminUser) return false;
    const state = String(d?.state || "").toUpperCase();
    if (!["SALES_LINKED", "WEIGH_TARE", "LOADED", "WEIGH_GROSS", "ACCOUNTS_PENDING"].includes(state)) {
      return false;
    }
    return String(d?.sales_settlement_status || "").toUpperCase() !== "FINAL";
  };

  const canAdminRemoveSalesLink = (d) => {
    if (!adminUser || d?.loaded_at) return false;
    return ["SALES_LINKED", "WEIGH_TARE"].includes(String(d?.state || "").toUpperCase());
  };

  const removeSalesLink = useCallback(async (row) => {
    const label = row.vehicle_number || row.gate_pass_code || row.code || "this truck";
    const keepTare = row.tare_weight_qtl != null;
    const ok = window.confirm(
      keepTare
        ? `Remove the sales link on ${label}? The truck returns to awaiting sales link. The tare weight stays and is used again after the next link.`
        : `Remove the sales link on ${label}? The truck returns to awaiting sales link.`,
    );
    if (!ok) return;
    try {
      await api.post(`/outward-dispatches/${row.id}/unlink-sales`);
      toast.success("Sales link removed");
      if (String(linkEditId) === String(row.id)) closeSalesLinkEdit();
      load();
      loadRegister({ background: true });
    } catch (err) {
      toast.error(extractError(err));
    }
  }, [closeSalesLinkEdit, linkEditId, load, loadRegister]);

  const byState = useMemo(() => {
    const rows = data.dispatches || [];
    const accountsPending = rows.filter((d) => d.state === "ACCOUNTS_PENDING");
    return {
      sales: rows.filter((d) => d.state === "REGISTERED"),
      loading: rows.filter((d) => d.state === "WEIGH_TARE"),
      accounts: accountsPending,
      dispatchVouchers: accountsPending.filter((d) => d.voucher_bucket === "dispatch" || !d.sales_voucher_id),
      provisionalVouchers: accountsPending.filter((d) => d.voucher_bucket === "provisional" || (d.sales_voucher_id && d.sales_settlement_status === "PROVISIONAL")),
      finalizedVouchers: (data.sales_voucher_register || []).filter(
        (d) => d.voucher_bucket === "finalized" || d.sales_settlement_status === "FINAL" || d.state === "COMPLETED",
      ),
      active: rows.filter((d) => !["COMPLETED", "REJECTED"].includes(d.state)),
    };
  }, [data.dispatches, data.sales_voucher_register]);

  const focusDispatch = useMemo(
    () => (data.dispatches || []).find((d) => String(d.id) === String(focusDispatchId)),
    [data.dispatches, focusDispatchId],
  );

  const salesLinkRegisterTable = useDataTable(
    data.sales_link_register || [],
    SALES_LINK_REGISTER_COLUMNS,
    { id: "linked_at", desc: true },
  );
  const loadingRegisterTable = useDataTable(
    data.loading_register || [],
    LOADING_REGISTER_COLUMNS,
    { id: "loaded_at", desc: true },
  );

  const registerCount =
    activeTab === "sales"
      ? (data.sales_link_register_count ?? (data.sales_link_register?.length || 0))
      : activeTab === "loading"
        ? (data.loading_register_count ?? (data.loading_register?.length || 0))
        : (data.sales_voucher_register_count ?? (data.sales_voucher_register?.length || 0));

  const registerBusy = pageTab === "register" && (registerLoading || (!registerLoaded && !loading));

  useEffect(() => {
    if (!focusDispatch) return;
    setPageTab("queue");
    requestAnimationFrame(() => {
      document.getElementById(`outward-dispatch-${focusDispatch.id}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [focusDispatch]);

  const queueCount =
    activeTab === "sales"
      ? byState.sales.length
      : activeTab === "loading"
        ? byState.loading.length
        : byState.dispatchVouchers.length + byState.provisionalVouchers.length;

  const queueLabel =
    activeTab === "sales" ? "Queue" : activeTab === "loading" ? "Loading" : "Queue";
  const registerLabel =
    activeTab === "sales"
      ? "Sales link register"
      : activeTab === "loading"
        ? "Loading register"
        : "Sales voucher register";

  const canWork =
    (activeTab === "sales" && canLinkSales) ||
    (activeTab === "loading" && canLoad) ||
    (activeTab === "accounts" && canAccounts);

  const isSalesVoucherDesk = activeTab === "accounts";

  return (
    <div className="space-y-6 sm:space-y-8" data-testid={moduleConfig.testId}>
      <PageHeader
        eyebrow={loadingDesk && activeTab === "loading" ? "Packing & Loading" : moduleConfig.eyebrow}
        title={moduleConfig.title}
        description={moduleConfig.description}
        actions={
          loadingDesk ? null : (
            <Button asChild variant="outline" className="min-h-10">
              <Link to="/gate/outward">Gate outward</Link>
            </Button>
          )
        }
      />

      {isSalesVoucherDesk ? (
        canAccounts ? (
          <AccountsSalesSettlementPanel hideDeskLink initialDispatchId={focusDispatchId || undefined} />
        ) : (
          <SectionCard title={moduleConfig.title}>
            <p className="text-sm text-muted-foreground py-6 text-center">
              You do not have permission to issue or finalize sales vouchers.
            </p>
          </SectionCard>
        )
      ) : (
        <>
      {!loadingDesk && (
        <div className="flex flex-wrap gap-2">
          <Pill>{queueCount} in queue</Pill>
          <Pill>{registerCount} in register</Pill>
          <Pill>{byState.active.length} active outward</Pill>
          {hasPermission(user, "page.weighbridge") && (
            <Button asChild variant="outline" size="sm">
              <Link to="/weighbridge/outward">Weighbridge (tare / gross)</Link>
            </Button>
          )}
        </div>
      )}

      <Tabs value={pageTab} onValueChange={setPageTab}>
        <TabsList className="bg-card border border-border p-1 h-auto w-full max-w-full justify-start overflow-x-auto flex-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsTrigger value="queue" className="text-xs sm:text-sm shrink-0 min-h-10">
            {queueLabel} ({queueCount})
          </TabsTrigger>
          <TabsTrigger value="register" className="text-xs sm:text-sm shrink-0 min-h-10">
            {registerLabel} ({registerCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="mt-4">
          {activeTab === "sales" && canWork && (
            <div className="space-y-4">
            <OutwardTruckBookingsPanel contracts={data.sales_contracts || []} onChanged={load} />
            <SectionCard title="Link party & sales contract" testId="outward-sales-queue">
              {loading ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
              ) : byState.sales.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No dispatches awaiting sales link.
                </p>
              ) : (
                <div className="space-y-3">
                  {byState.sales.map((d) => (
                    <DispatchCard key={d.id} d={d} focused={String(focusDispatchId) === String(d.id)}>
                      <SalesLinkPanel dispatch={d} contracts={data.sales_contracts || []} onSaved={load} />
                    </DispatchCard>
                  ))}
                </div>
              )}
            </SectionCard>
            </div>
          )}

          {activeTab === "loading" && canWork && (
            <SectionCard title="Confirm loading" testId="outward-loading-queue">
              {loading ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
              ) : byState.loading.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No trucks awaiting loading.</p>
              ) : (
                <div className="space-y-3">
                  {byState.loading.map((d) => (
                    <DispatchCard key={d.id} d={d} focused={String(focusDispatchId) === String(d.id)}>
                      <LoadingPanel dispatch={d} onSaved={load} />
                    </DispatchCard>
                  ))}
                </div>
              )}
            </SectionCard>
          )}

          {!canWork && (
            <SectionCard title={moduleConfig.title}>
              <p className="text-sm text-muted-foreground py-6 text-center">
                You do not have permission for this queue. You can still browse the register tab.
              </p>
            </SectionCard>
          )}
        </TabsContent>

        <TabsContent value="register" className="mt-4">
          {activeTab === "sales" && (
            <SectionCard title="Sales link register" testId="sales-link-register">
              <p className="text-xs text-muted-foreground mb-3">
                Past vehicles linked to a party and sales contract — including trucks already moved downstream.
                {adminUser
                  ? " Admin can edit or change the contract until final settlement, and remove the link before loading."
                  : ""}
              </p>
              <DataTableBlock
                title=""
                wrapInSectionCard={false}
                columns={SALES_LINK_REGISTER_COLUMNS}
                table={salesLinkRegisterTable}
                loading={registerBusy}
                testId="sales-link-register-table"
                minWidth="920px"
                emptyMessage="No sales links recorded yet."
                renderTrailingHeader={
                  adminUser ? (
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Actions
                    </span>
                  ) : null
                }
                renderRow={(d) => (
                  <React.Fragment key={d.id}>
                    <tr className="border-b border-border/60 hover:bg-muted/20">
                      <td className="py-2 px-2 whitespace-nowrap">
                        {formatListDate(d.sales_linked_at || d.created_at) || "—"}
                        {(d.sales_linked_at || d.created_at) && (
                          <div className="text-[10px] text-muted-foreground">
                            {formatIndiaDateTime(d.sales_linked_at || d.created_at)}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 font-medium uppercase">{d.vehicle_number || "—"}</td>
                      <td className="py-2 px-2 font-mono">{d.gate_pass_code || "—"}</td>
                      <td className="py-2 px-2">{d.party_name || "—"}</td>
                      <td className="py-2 px-2 font-mono">{d.sales_contract_number || "—"}</td>
                      <td className="py-2 px-2">{d.variety || "—"}</td>
                      <td className="py-2 px-2">
                        <StatePill state={d.state} label={STATE_LABELS[d.state] || d.state} />
                      </td>
                      <td className="py-2 px-2">{d.sales_linked_by || "—"}</td>
                      {adminUser && (
                        <td className="py-2 px-2 whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            {canAdminEditSalesLink(d) ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                data-testid={`sales-link-edit-${d.id}`}
                                onClick={() =>
                                  String(linkEditId) === String(d.id)
                                    ? closeSalesLinkEdit()
                                    : openSalesLinkEdit(d)
                                }
                              >
                                {String(linkEditId) === String(d.id) ? "Close" : "Edit / change"}
                              </Button>
                            ) : null}
                            {canAdminRemoveSalesLink(d) ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                data-testid={`sales-link-remove-${d.id}`}
                                onClick={() => removeSalesLink(d)}
                              >
                                Remove
                              </Button>
                            ) : null}
                            {!canAdminEditSalesLink(d) && !canAdminRemoveSalesLink(d) ? (
                              <span className="text-[10px] text-muted-foreground">—</span>
                            ) : null}
                          </div>
                        </td>
                      )}
                    </tr>
                    {adminUser && String(linkEditId) === String(d.id) && (
                      <tr className="border-b border-border/60 bg-muted/10">
                        <td colSpan={SALES_LINK_REGISTER_COLUMNS.length + 1} className="p-3">
                          {linkEditLoading ? (
                            <p className="text-sm text-muted-foreground py-4 text-center">Loading link…</p>
                          ) : linkEditDispatch ? (
                            <SalesLinkPanel
                              dispatch={linkEditDispatch}
                              contracts={data.sales_contracts || []}
                              onSaved={onSalesLinkEdited}
                            />
                          ) : (
                            <p className="text-sm text-muted-foreground py-4 text-center">Could not load dispatch.</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )}
              />
            </SectionCard>
          )}

          {activeTab === "loading" && (
            <SectionCard title="Loading register" testId="outward-loading-register">
              <p className="text-xs text-muted-foreground mb-3">
                Recorded loads. A truck with several batches or items lists every line.
                {adminUser ? " Admin can correct a recorded load." : ""}
              </p>
              <DataTableBlock
                title=""
                wrapInSectionCard={false}
                columns={LOADING_REGISTER_COLUMNS}
                table={loadingRegisterTable}
                loading={registerBusy}
                testId="loading-register-table"
                minWidth="980px"
                emptyMessage="No loading records yet."
                renderTrailingHeader={() => (
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Actions</span>
                )}
                renderRow={(d) => {
                  const lines = Array.isArray(d.load_lines) && d.load_lines.length ? d.load_lines : null;
                  const cell = (pick) =>
                    lines ? (
                      <div className="space-y-1">
                        {lines.map((line) => (
                          <div key={line.id || `${line.production_batch_id}-${line.item_id}-${line.weight_per_bag}`}>
                            {pick(line)}
                          </div>
                        ))}
                      </div>
                    ) : null;
                  return (
                    <React.Fragment key={d.id}>
                      <tr className="border-b border-border/60 hover:bg-muted/20 align-top">
                        <td className="py-2 px-2 whitespace-nowrap">
                          {formatListDate(d.loaded_at) || "—"}
                          {d.loaded_at && (
                            <div className="text-[10px] text-muted-foreground">
                              {formatIndiaDateTime(d.loaded_at)}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-2 font-medium uppercase">{d.vehicle_number || "—"}</td>
                        <td className="py-2 px-2 font-mono">{d.gate_pass_code || "—"}</td>
                        <td className="py-2 px-2">{d.party_name || "—"}</td>
                        <td className="py-2 px-2 font-mono">
                          {cell((line) => line.production_batch_code || "—") || d.production_batch_code || "—"}
                        </td>
                        <td className="py-2 px-2">
                          {cell((line) => line.crop_name || "—") || d.crop_name || "—"}
                        </td>
                        <td className="py-2 px-2">
                          {cell((line) => line.item_name || "—") || d.item_name || "—"}
                        </td>
                        <td className="py-2 px-2 tabular">
                          {cell((line) => line.bags_loaded ?? "—") || (d.bags_loaded ?? "—")}
                        </td>
                        <td className="py-2 px-2">
                          {cell((line) => (line.weight_per_bag ? `${line.weight_per_bag} kg` : "—")) || d.packing_label || "—"}
                        </td>
                        <td className="py-2 px-2 tabular">
                          {cell((line) => line.weight_loaded_qtl ?? "—") || (d.net_weight_qtl ?? "—")}
                        </td>
                        <td className="py-2 px-2">{d.loaded_by || "—"}</td>
                        <td className="py-2 px-2 text-right whitespace-nowrap">
                          <div className="inline-flex items-center justify-end gap-1">
                            {adminUser ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                data-testid={`loading-edit-${d.id}`}
                                onClick={() =>
                                  setLoadEditId((cur) => (String(cur) === String(d.id) ? null : d.id))
                                }
                              >
                                {String(loadEditId) === String(d.id) ? "Close" : "Edit"}
                              </Button>
                            ) : null}
                            <LoadingReceiptActions
                              dispatchId={d.id}
                              label={d.gate_pass_code || d.code}
                              compact
                            />
                          </div>
                        </td>
                      </tr>
                      {adminUser && String(loadEditId) === String(d.id) ? (
                        <tr className="border-b border-border/60 bg-muted/10">
                          <td colSpan={LOADING_REGISTER_COLUMNS.length + 1} className="p-3">
                            <LoadingPanel
                              dispatch={d}
                              revise
                              onSaved={() => {
                                setLoadEditId(null);
                                loadRegister();
                              }}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                }}
              />
            </SectionCard>
          )}

        </TabsContent>
      </Tabs>
        </>
      )}
    </div>
  );
}
